import * as Progress from '@radix-ui/react-progress';
import { composeVariants, cva, cx, type VariantProps } from '../../utils/cva';

const baseVariant = cva({
  base: 'relative overflow-hidden rounded-full bg-default-active',
});
const progressVariants = cva({
  variants: {
    size: {
      sm: 'h-1',
      md: 'h-2',
      lg: 'h-3',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const progessBarVariants = composeVariants(progressVariants, baseVariant);

export interface LinearProgressBarßProps
  extends VariantProps<typeof progessBarVariants> {
  /**
   * Current progress value (from 0 to 100).
   */
  value?: number;
  /**
   * Optional text label to display below the progress bar.
   */
  label?: string;
  /**
   * Size variant for the progress bar.
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Additional CSS classes for styling.
   */
  className?: string;
  /**
   * Data-testid for testing purposes.
   */
  'data-testid': string | null;
}

export function LinearProgressBarß({
  value = 0,
  size = 'md',
  label,
  className,
  'data-testid': dataTestId,
}: LinearProgressBarßProps) {
  const testId = dataTestId ?? 'linear-spinner';
  const progressValue = Math.min(Math.max(value, 0), 100);

  return (
    <div className={cx(label && 'flex flex-col gap-3')}>
      <Progress.Root
        value={progressValue}
        data-testid={`${testId}-linear-loader`}
        className={cx(
          progressVariants({ size }),
          'w-full bg-default-hover relative overflow-hidden rounded-full',
          className,
        )}
      >
        <Progress.Indicator
          className="h-full bg-primary transition-all duration-200 ease-linear"
          style={{ transform: `translateX(-${100 - progressValue}%)` }}
        />
      </Progress.Root>
      {label && (
        <span data-testid={`${testId}-label`} className="text-default">
          {label}
        </span>
      )}
    </div>
  );
}
